<template>
  <DialogHeader> Remove author? </DialogHeader>
  <DialogContent>
    Are you sure you want to remove {{ user }}
    as an author of this file?
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      danger
      :loading="loading"
      @click="handleClick"
    >
      Remove
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Icon, UiButton } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { graphql } from '@/shared/graphql/router'
import {
  InviteType,
  type AuthorsConnection,
  type InviteInput,
} from '@/shared/graphql/router/graphql'
import type { Reference } from '@apollo/client'
import { useMutation } from '@vue/apollo-composable'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'
import { computed } from 'vue'

const REMOVE_AUTHOR_MUTATION = graphql(`
  mutation RemoveAuthor($input: RemoveAuthorInput!) {
    removeAuthor(input: $input) {
      success
    }
  }
`)

const { close, triggerEvent } = useDialog()
const { closeAll } = useDialog()

const props = defineProps<{
  fileId: string
  invite: InviteInput
  name: string
}>()

const { mutate: removeAuthor, loading } = useMutation(REMOVE_AUTHOR_MUTATION, {
  clientId: 'router',
})

const toastStore = useToastStore()
const userStore = useUserStore()

const user = computed(() =>
  props.invite.id === userStore.user?.id ? 'yourself' : `${props.name}`
)

const handleClick = async () => {
  try {
    await removeAuthor(
      { input: { fileId: props.fileId, invite: props.invite } },
      {
        update(cache) {
          cache.modify({
            id: cache.identify({ __typename: 'File', id: props.fileId }),
            fields: {
              authors(existing, { readField }) {
                const data = existing as AuthorsConnection
                if (!data?.edges) return existing

                return {
                  ...data,
                  edges: data.edges.filter(({ node }) => {
                    if (props.invite.type === InviteType.Email) {
                      return readField('email', node) !== props.invite.email
                    }

                    const entity = readField<Reference>('entity', node)
                    return entity
                      ? readField('id', entity) !== props.invite.id
                      : true
                  }),
                }
              },
            },
          })
        },
      }
    )

    if (props.invite.id === userStore.user?.id) {
      closeAll()
      return
    }

    triggerEvent('success', {})
    close()
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not remove author',
      error: error as Error,
    })
  }
}
</script>
